// Das Übungsufer (K46, Kampf-Designer mit Fabi, 25./26.10.2026): Echtzeit mit Pause. Man stellt kurz auf, dann läuft
// der Kampf live; mit der Leertaste hält man an und gibt in Ruhe Befehle: Freund wählen (Klick, Bild in der Leiste oder
// 1 bis 4), dann ein Klick auf den Boden (hingehen) oder auf ein Sturmwesen (angreifen). Q oder der Knopf in der Leiste
// zielt mit dem Kniff, eine Vorschau zeigt vorher, was passiert. Rechtsklick oder Esc löscht den Befehl.
// Der Run bleibt, wie er ist. Reine Darstellung und Eingabe: Befehle gehen als Eingaben in die Simulation.
import { combatRules, getAbility, getEnemy, getSpecies, practice } from '../../content/index.ts';
import { FrameFlag, FrameFlag2, kniffAim, type BattleResult, type Obstacle, type PracticePlacement } from '../../sim/index.ts';
import { image, pixelsPerUnit, sprite, sprites } from '../art.ts';
import { drawArtEffects, figureBox, figureGeo } from '../figures.ts';
import { FONT, drawEffects, drawGround, drawUnits, CLASS_COLORS, type DrawUnit } from '../render.ts';
import { BASE_SPEED } from '../shore/state.ts';
import { enemyNames } from '../summary.ts';
import { setSetting, settings } from '../settings.ts';
import { applyTwig, setTwigText } from '../twig.ts';
import { drawBeckon, drawGroundSigns, drawLandingShadows, obstacleProps, drawOrders, drawPreview, drawRings, drawTelegraphs, telegraphsAt, type Preview } from './draw.ts';
import { applyFrameArt, cursorFor, drawLeaves, drawPracticeGround, drawPracticeLight, kniffIcon } from './look.ts';
import { advance, calmEvents, command, duration, ended, eventsIn, frameIndex, ordersAt, startLive, unitsAt, type LiveView } from './live.ts';

type Phase = 'setup' | 'battle' | 'end';
type Aim = { uid: number; kniff: string; kind: 'place' | 'friend' | 'enemy' };

const FIELD = combatRules.field;
/** Namen der Sturmwesen: Wer allein ist, heißt nach seiner Art; die drei Schlammlinge haben Spitznamen (G30) */
const NICKS = enemyNames(practice.enemies.map((e) => e.enemy));
const ENEMY_NAMES = practice.enemies.map((e, j) => (practice.enemies.filter((o) => o.enemy === e.enemy).length > 1 ? NICKS[j]! : getEnemy(e.enemy).name));
/** „Der Treibholzer“, „Das Hagelchen“, „Blubb“ */
const theName = (j: number): string => {
  const e = practice.enemies[j]!;
  const def = getEnemy(e.enemy);
  const art = def.article ?? 'der';
  return ENEMY_NAMES[j] === def.name ? `${art.charAt(0).toUpperCase()}${art.slice(1)} ${def.name}` : ENEMY_NAMES[j]!;
};
const FRIENDS = practice.friends.length;
const OBSTACLES: Obstacle[] = practice.obstacles.map(({ id, kind, x, y, radius, x2, y2 }) => ({ id, kind, x, y, radius, ...(x2 !== undefined ? { x2, y2 } : {}) }));
/** Spielgefühl (Vorschlag, Fabi beurteilt es beim Test) [ZAHL?] */
const FEEL = { hitStopMs: 70, swoopStopMs: 110, slowSeconds: 0.5, slowFactor: 0.3, shakeMs: 260, shakePx: 5 };
/** So lange nach dem Ende läuft das Bild weiter, damit Vertriebene verpuffen und Gefallene sinken */
const AFTER = 1.8;

const P = {
  phase: 'setup' as Phase,
  placements: practice.friends.map((f) => ({ x: f.x, y: f.y })) as PracticePlacement[],
  drag: null as null | { i: number; dx: number; dy: number },
  view: null as LiveView | null,
  showT: 0,
  paused: true,
  selected: null as number | null,
  aiming: null as Aim | null,
  cursor: { x: 0, y: 0, inside: false },
  hover: null as number | null,
  hint: '',
  hintKind: '' as '' | 'warn' | 'ready',
  /** Ereignisse, bei denen das Spiel schon von selbst angehalten hat */
  autoSeen: new Set<string>(),
  stopUntil: 0,
  slowUntil: 0,
  shakeUntil: 0,
  /** große Zahlen bei Schwachstellen und Sturzflug (Anzeigezeit, Ort, Text) */
  bigs: [] as { t: number; x: number; y: number; text: string; color: string }[],
  attempts: 0,
  units: [] as DrawUnit[],
};

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
let canvas: HTMLCanvasElement;
let opened = false;
let leave: () => void = () => {};

// ---------- Öffnen und Schließen ----------

export function initPractice(hooks: { toTitle: () => void }): void {
  leave = hooks.toTitle;
  canvas = $<HTMLCanvasElement>('pr-field');
  canvas.addEventListener('pointermove', (ev) => {
    const p = toField(ev);
    P.cursor = { ...p, inside: true };
    if (P.drag) {
      const zone = FIELD.friendZone;
      P.placements[P.drag.i] = { x: clamp(p.x + P.drag.dx, zone.xMin + 3, zone.xMax - 1), y: clamp(p.y + P.drag.dy, 5, FIELD.height - 5) };
    }
  });
  canvas.addEventListener('pointerleave', () => (P.cursor.inside = false));
  canvas.addEventListener('pointerdown', (ev) => {
    if (ev.button === 2) return;
    onPointerDown(toField(ev));
  });
  window.addEventListener('pointerup', () => (P.drag = null));
  canvas.addEventListener('contextmenu', (ev) => {
    ev.preventDefault();
    clearSelectedOrder();
  });
  $('pr-pause').addEventListener('click', () => togglePause());
  $('pr-start').addEventListener('click', () => startBattle());
  $('pr-again').addEventListener('click', () => toSetup());
  $('pr-back').addEventListener('click', () => leave());
  $('pr-leave').addEventListener('click', () => leave());
  $('pr-auto').addEventListener('click', () => {
    setSetting('autoPause', !settings().autoPause);
    renderTop();
  });
  $('pr-bar').addEventListener('click', (ev) => {
    const el = ev.target as HTMLElement;
    const card = el.closest<HTMLElement>('[data-uid]');
    if (!card) return;
    const uid = Number(card.dataset.uid);
    if (el.closest('[data-kniff]')) return startAim(uid);
    select(uid);
  });
}

export function openUebungsufer(): void {
  opened = true;
  applyTwig(document.getElementById('view-practice')!);
  applyFrameArt();
  toSetup();
}

function toSetup(): void {
  P.phase = 'setup';
  P.view = null;
  P.selected = null;
  P.aiming = null;
  P.hint = '';
  document.body.dataset.prPhase = 'setup';
  renderTop();
  renderBar();
}

function startBattle(): void {
  P.attempts += 1;
  P.view = startLive(P.placements, practice.seed);
  P.showT = 0;
  P.paused = true;
  P.selected = null;
  P.aiming = null;
  P.autoSeen = new Set();
  P.bigs = [];
  P.stopUntil = P.slowUntil = P.shakeUntil = 0;
  P.phase = 'battle';
  document.body.dataset.prPhase = 'battle';
  setHint('Der Kampf steht still, bis du die Leertaste drückst. In der Pause wählst du in Ruhe einen Freund und klickst auf den Boden, damit er dorthin geht, oder auf ein Sturmwesen, damit er es angreift.', 'ready');
  advance(P.view, 0);
  renderTop();
  renderBar();
}

function finish(): void {
  const r = P.view!.result;
  const won = r.outcome === 'win';
  P.phase = 'end';
  document.body.dataset.prPhase = 'end';
  const standing = r.units.filter((u) => u.side === 'friend' && u.alive).map((u) => u.name);
  const text = won
    ? standing.length === 4
      ? 'Alle vier stehen noch, und das Ufer ist frei. Das war ein schöner Zug nach dem anderen.'
      : `Das Ufer ist frei. ${standing.join(' und ')} ${standing.length === 1 ? 'hat' : 'haben'} durchgehalten, die anderen ruhen sich am Floß aus.`
    : 'Die Sturmwesen waren diesmal stärker. Probier eine andere Aufstellung oder eine andere Reihenfolge der Kniffe.';
  pin($('pr-end'));
  setTwigText($('pr-end-title'), won ? 'Geschafft!' : 'Diesmal nicht');
  $('pr-end-text').textContent = text;
  $('pr-end-time').textContent = `Der Kampf hat ${Math.round(duration(P.view!))} Sekunden gedauert.`;
  const list = $('pr-end-friends');
  list.replaceChildren(
    ...r.friends.map((f) => {
      const li = document.createElement('li');
      li.classList.toggle('down', f.fell);
      const face = document.createElement('div');
      face.className = 'pr-end-face';
      face.append(portrait(f.species));
      const p = document.createElement('p');
      p.textContent = deeds(r, f);
      li.append(face, p);
      if (f.fell) {
        const rest = document.createElement('small');
        rest.textContent = 'ruht sich aus';
        li.append(rest);
      }
      return li;
    }),
  );
  renderTop();
}

const TIMES = ['keinmal', 'einmal', 'zweimal', 'dreimal', 'viermal', 'fünfmal', 'sechsmal'];
const times = (n: number) => TIMES[n] ?? `${n}-mal`;
/** Was ein Kniff getan hat, als Satzteil nach „hat …“ */
const KNIFF_DEED: Record<string, (n: string) => string> = {
  mulde: (n) => `${n} seinen Bau gegraben`,
  ball: (n) => `sich ${n} zur Stachelkugel gerollt`,
  beckon: (n) => `${n} jemanden zu sich gewinkt und die Wunden ausgewaschen`,
  weakspot: (n) => `${n} eine Schwachstelle ausgemacht`,
};

/** Ein warmer Satz je Freund für den Zettel am Ende: was er getan hat, in ganzen Sätzen statt Zahlenkolonnen */
function deeds(r: BattleResult, f: BattleResult['friends'][number]): string {
  const parts: string[] = [];
  const st = f.stats;
  if (st.damage >= 1) parts.push(`${Math.round(st.damage)} Schaden gemacht`);
  if (st.shielded >= 1) parts.push(`${Math.round(st.shielded)} Schaden abgehalten`);
  if (st.healed >= 1) parts.push(`${Math.round(st.healed)} Leben geheilt`);
  const u = r.units[f.uid];
  const mech = (u?.kniff ? getAbility(u.kniff).mechanic : undefined) ?? '';
  if (f.kniffUses > 0 && KNIFF_DEED[mech]) parts.push(KNIFF_DEED[mech]!(times(f.kniffUses)));
  const swoops = r.events.filter((e) => e.type === 'swoop' && e.unit === f.uid).length;
  // „Lia hat 380 Schaden gemacht, zweimal eine Schwachstelle ausgemacht und ist zweimal im Sturzflug hinterhergestürzt.“
  const swoop = swoops ? `ist ${times(swoops)} im Sturzflug hinterhergestürzt` : '';
  let text: string;
  if (!parts.length) text = swoop ? `${f.name} ${swoop}` : `${f.name} hat diesmal kaum etwas ausrichten können`;
  else if (swoop) text = `${f.name} hat ${parts.join(', ')} und ${swoop}`;
  else text = `${f.name} hat ${joinAnd(parts)}`;
  return `${text}.`;
}

const joinAnd = (xs: string[]) => (xs.length < 2 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} und ${xs[xs.length - 1]}`);

// ---------- Eingabe ----------

const clamp = (x: number, a: number, b: number) => Math.min(b, Math.max(a, x));
function toField(ev: { clientX: number; clientY: number }): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  return { x: ((ev.clientX - rect.left) / rect.width) * FIELD.width, y: ((ev.clientY - rect.top) / rect.height) * FIELD.height };
}

/** Wer unter dem Zeiger steht (mit Figuren zählt der Körper) */
function unitAt(p: { x: number; y: number }, filter: (u: DrawUnit) => boolean = () => true): DrawUnit | null {
  const scale = canvas.width / FIELD.width;
  let best: DrawUnit | null = null;
  let bestD = Infinity;
  for (const u of P.units) {
    if ((u.flags & FrameFlag.alive) === 0 || u.kind === 'tree' || !filter(u)) continue;
    const box = figureBox(u, scale);
    const inBox = box && p.x >= box.x0 && p.x <= box.x1 && p.y >= box.y0 && p.y <= box.y1;
    const d = Math.hypot(u.x - p.x, u.y - p.y);
    if ((inBox || d < u.radius + 2) && d < bestD) {
      best = u;
      bestD = d;
    }
  }
  return best;
}

function onPointerDown(p: { x: number; y: number }): void {
  if (P.phase === 'setup') {
    // Aufstellen: Freunde ziehen
    let best = -1;
    let bestD = Infinity;
    P.placements.forEach((q, i) => {
      const d = Math.hypot(q.x - p.x, q.y - 2 - p.y);
      if (d < 7 && d < bestD) {
        best = i;
        bestD = d;
      }
    });
    if (best >= 0) P.drag = { i: best, dx: P.placements[best]!.x - p.x, dy: P.placements[best]!.y - p.y };
    return;
  }
  if (P.phase !== 'battle' || !P.view) return;
  if (P.aiming) return fireAim(p);
  const hit = unitAt(p);
  if (hit && hit.side === 'friend') return select(hit.uid);
  if (P.selected === null) {
    if (!hit) setHint('Wähl erst einen Freund: ein Klick auf ihn, sein Bild unten oder die Tasten 1 bis 4.', '');
    return;
  }
  if (hit && hit.side === 'enemy') {
    command(P.view, P.showT, { unit: P.selected, kind: 'attack', target: hit.uid });
    setHint(`${nameOf(P.selected)} greift ${hit.uid >= FRIENDS ? ENEMY_NAMES[hit.uid - FRIENDS] : hit.name} an.`, '');
  } else {
    command(P.view, P.showT, { unit: P.selected, kind: 'move', x: p.x, y: p.y });
    setHint(`${nameOf(P.selected)} geht dorthin.`, '');
  }
  renderBar();
}

const nameOf = (uid: number) => P.view?.result.units[uid]?.name ?? '';

function select(uid: number | null): void {
  if (P.phase !== 'battle') return;
  const u = uid === null ? null : P.view?.result.units[uid];
  if (u && (!u.alive || u.side !== 'friend')) return;
  P.selected = uid;
  P.aiming = null;
  if (u) setHint(`${u.name} wartet auf deinen Befehl: Klick auf den Boden heißt hingehen, auf ein Sturmwesen angreifen${readyOf(u.uid) ? ', Q zielt mit dem Kniff' : ''}.`, '');
  renderBar();
}

function readyOf(uid: number): boolean {
  const r = P.view?.result;
  if (!r) return false;
  const f = r.frames[frameIndex(r, P.showT)];
  return ((f?.units[uid]?.[8] ?? 0) & FrameFlag2.ready) !== 0 && ((f?.units[uid]?.[4] ?? 0) & FrameFlag.alive) !== 0;
}

function startAim(uid: number): void {
  if (P.phase !== 'battle' || !P.view) return;
  const u = P.view.result.units[uid];
  if (!u || !u.alive || !u.kniff) return;
  P.selected = uid;
  if (!readyOf(uid)) {
    setHint(`${u.name} braucht noch etwas mehr Mut für ${getAbility(u.kniff).name}. Der goldene Ring zeigt, wann es so weit ist.`, '');
    renderBar();
    return;
  }
  const kind = kniffAim(u.kniff);
  if (kind === 'none') return;
  P.aiming = { uid, kniff: u.kniff, kind };
  const what = kind === 'place' ? 'einen Ort' : kind === 'friend' ? 'einen Freund' : 'ein Sturmwesen';
  setHint(`${getAbility(u.kniff).name}: Zeig auf ${what} und klick. Rechtsklick oder Esc bricht ab.`, 'ready');
  renderBar();
}

function fireAim(p: { x: number; y: number }): void {
  const a = P.aiming!;
  const pv = preview();
  if (!pv) return;
  if (a.kind === 'place') command(P.view!, P.showT, { unit: a.uid, kind: 'kniff', x: pv.at.x, y: pv.at.y });
  else if (pv.target) command(P.view!, P.showT, { unit: a.uid, kind: 'kniff', target: pv.target.uid });
  else return setHint(a.kind === 'friend' ? 'Klick auf einen Freund, den er zu sich winken soll.' : 'Klick auf ein Sturmwesen.', '');
  void p;
  setHint(fired(a, pv), 'ready');
  P.aiming = null;
  renderBar();
}

/** Was nach dem Zünden passiert, als kleiner Satz in der Stimme des Spiels */
function fired(a: Aim, pv: Preview): string {
  const who = nameOf(a.uid);
  const them = pv.target ? (pv.target.side === 'enemy' ? (ENEMY_NAMES[pv.target.uid - FRIENDS] ?? pv.target.name) : pv.target.name) : '';
  switch (getAbility(a.kniff).mechanic) {
    case 'mulde':
      return `${who} gräbt sich ein und wühlt sich hinüber. Gleich sackt dort der Boden ab.`;
    case 'ball':
      return `${who} rollt sich zur Stachelkugel zusammen und saust los.`;
    case 'beckon':
      return `${who} winkt ${them} zu sich, um die Wunden auszuwaschen.`;
    case 'weakspot':
      return `${who} hat bei ${them} eine Schwachstelle entdeckt. Ein Kniff darauf trifft doppelt.`;
    default:
      return `${who} setzt ${getAbility(a.kniff).name} ein.`;
  }
}

function clearSelectedOrder(): boolean {
  if (P.aiming) {
    P.aiming = null;
    setHint('Du hast das Zielen abgebrochen, es bleibt alles, wie es war.', '');
    renderBar();
    return true;
  }
  if (P.phase === 'battle' && P.selected !== null && P.view) {
    command(P.view, P.showT, { unit: P.selected, kind: 'clear' });
    setHint(`${nameOf(P.selected)} handelt wieder selbst.`, '');
    P.selected = null;
    renderBar();
    return true;
  }
  return false;
}

/** Esc: erst Zielen oder Befehl löschen, dann (sonst) das Menü (T35) */
export function practiceEsc(): boolean {
  if (!opened || document.body.dataset.view !== 'practice') return false;
  return clearSelectedOrder();
}

function togglePause(): void {
  if (P.phase === 'setup') return startBattle();
  if (P.phase !== 'battle') return;
  P.paused = !P.paused;
  if (!P.paused) setHint('', '');
  renderTop();
}

/** Tasten und Controller (X10): Leertaste anhalten, 1 bis 4 wählen, Q Kniff, Rücktaste Befehl löschen, Enter los */
export function practiceAction(a: string): void {
  if (P.phase === 'end') {
    if (a === 'confirm' || a === 'start') toSetup();
    if (a === 'back') leave();
    return;
  }
  if (P.phase === 'setup') {
    if (a === 'confirm' || a === 'start') startBattle();
    if (a === 'back') leave();
    return;
  }
  switch (a) {
    case 'confirm':
      return togglePause();
    case 'speed1':
    case 'speed2':
    case 'speed3':
    case 'speed4': {
      const uid = Number(a.slice(-1)) - 1;
      return P.selected === uid && !P.aiming ? startAim(uid) : select(uid);
    }
    case 'prev':
      if (P.selected !== null) startAim(P.selected);
      else setHint('Wähl erst einen Freund, dann Q für seinen Kniff.', '');
      return;
    case 'back':
      clearSelectedOrder();
      return;
  }
}

// ---------- Vorschau ----------

function preview(): Preview | null {
  const a = P.aiming;
  if (!a || !P.view) return null;
  const actor = P.units[a.uid];
  if (!actor) return null;
  const k = getAbility(a.kniff);
  const foes = P.units.filter((u) => u.side === 'enemy' && (u.flags & FrameFlag.alive) !== 0 && u.kind !== 'tree');
  const mates = P.units.filter((u) => u.side === 'friend' && (u.flags & FrameFlag.alive) !== 0 && u.kind !== 'tree');
  const pv: Preview = { actor, kniff: a.kniff, at: { x: P.cursor.x, y: P.cursor.y }, hit: [], guarded: [], target: null, choices: [] };
  if (a.kind === 'place') {
    const reach = k.params.reach!;
    const dx = P.cursor.x - actor.x;
    const dy = P.cursor.y - actor.y;
    const d = Math.hypot(dx, dy);
    const s = d > reach ? reach / d : 1;
    pv.at = { x: actor.x + dx * s, y: actor.y + dy * s };
    const R = k.params.radius!;
    pv.hit = foes.filter((u) => Math.hypot(u.x - pv.at.x, u.y - pv.at.y) - u.radius - actor.radius <= R);
    if (k.mechanic === 'mulde') pv.guarded = mates.filter((u) => u !== actor && Math.hypot(u.x - pv.at.x, u.y - pv.at.y) <= k.params.guardRadius!);
  } else if (a.kind === 'friend') {
    pv.choices = mates.filter((u) => u !== actor);
    pv.target = unitAt(P.cursor, (u) => u.side === 'friend' && u !== actor);
  } else {
    pv.choices = foes;
    pv.target = unitAt(P.cursor, (u) => u.side === 'enemy');
  }
  return pv;
}

// ---------- Ablauf ----------

function setHint(text: string, kind: '' | 'warn' | 'ready'): void {
  P.hint = text;
  P.hintKind = kind;
  const el = $('pr-hint');
  // T38: Ein neuer Zettel gleitet herein, federt nach und wird angeheftet; wer nur den Text tauscht, bleibt hängen
  const fresh = !!text && (el.hidden || el.dataset.kind !== kind);
  el.textContent = text;
  el.dataset.kind = kind;
  el.hidden = !text;
  if (fresh) pin(el);
}

/** T38: den Zettel neu anheften (die Bewegung steht in style.css, .pinning) */
function pin(el: HTMLElement): void {
  el.classList.remove('pinning');
  void el.offsetWidth;
  el.classList.add('pinning');
}

/** Selbst anhalten (Vorschlag, Schalter in den Einstellungen): wenn ein Kniff bereit wird oder ein großer Angriff kommt */
function autoPauseIn(r: BattleResult, a: number, b: number): { t: number; text: string; kind: 'warn' | 'ready' } | null {
  if (!settings().autoPause) return null;
  let best: { t: number; text: string; kind: 'warn' | 'ready' } | null = null;
  for (const e of eventsIn(r, a, b)) {
    if (e.type !== 'telegraph') continue;
    const key = `t${e.id}`;
    if (P.autoSeen.has(key)) continue;
    P.autoSeen.add(key);
    const who = theName(e.unit - FRIENDS);
    const target = e.shape.kind === 'mark' ? r.units[e.shape.target]?.name : null;
    const text =
      e.kniff === 'rammstoss' ? `${who} holt zum Rammstoß aus! Wer im violetten Streifen steht, wird getroffen und weggeschubst.`
      : e.kniff === 'sprung' ? `${who} will ${target ? `auf ${target}` : 'nach hinten'} springen!`
      : e.kniff === 'hagelschauer' ? `${who} sagt einen Hagelschauer an, gleich fliegen viele Körner.`
      : `${who} holt zu einem ${getAbility(e.kniff).name} ${target ? `gegen ${target} ` : ''}aus!`;
    best = { t: e.t, text, kind: 'warn' };
    break;
  }
  // Kniff bereit: Der Mut eines Freundes ist eben voll geworden
  const tick = combatRules.tickSeconds;
  const i0 = frameIndex(r, a);
  const i1 = frameIndex(r, b);
  for (let i = i0 + 1; i <= i1 && !best; i++) {
    const f = r.frames[i]!;
    const prev = r.frames[i - 1]!;
    for (const u of r.units) {
      if (u.side !== 'friend' || u.kind === 'tree') continue;
      const now = ((f.units[u.uid]?.[8] ?? 0) & FrameFlag2.ready) !== 0;
      const was = ((prev.units[u.uid]?.[8] ?? 0) & FrameFlag2.ready) !== 0;
      if (!now || was) continue;
      const key = `r${u.uid}:${i}`;
      if (P.autoSeen.has(key)) continue;
      P.autoSeen.add(key);
      best = { t: i * tick, text: `${u.name} ist bereit für ${getAbility(u.kniff!).name}. Wähl ${u.name} aus und drück Q, oder klick unten auf den Knopf.`, kind: 'ready' };
      break;
    }
  }
  return best;
}

/** Spielgefühl: Trefferstopp, Zeitlupe bei Schwachstellen, Wackeln beim Sturzflug, große Zahlen */
function feel(r: BattleResult, a: number, b: number): void {
  const now = performance.now();
  for (const e of eventsIn(r, a, b)) {
    if (e.type !== 'damage' || e.from === null) continue;
    const from = r.units[e.from]!;
    if (from.side !== 'friend' || e.source !== 'kniff') continue;
    const to = P.units[e.to];
    if (e.spotOpen) {
      P.slowUntil = e.t + FEEL.slowSeconds;
      P.stopUntil = Math.max(P.stopUntil, now + FEEL.swoopStopMs);
    } else P.stopUntil = Math.max(P.stopUntil, now + FEEL.hitStopMs);
    if (e.via === 'passive:sturzflug-folge') {
      P.shakeUntil = now + FEEL.shakeMs;
      P.stopUntil = Math.max(P.stopUntil, now + FEEL.swoopStopMs);
      if (to) P.bigs.push({ t: e.t, x: to.x, y: to.y - to.radius * 2.4, text: `${Math.round(e.amount)}`, color: '#ffd27a' });
    } else if (e.spotOpen && to) P.bigs.push({ t: e.t, x: to.x, y: to.y - to.radius * 2.2, text: 'Schwachstelle!', color: '#ff9e7a' });
  }
}

export function practiceFrame(dt: number): void {
  if (!opened || document.body.dataset.view !== 'practice') return;
  if (P.phase !== 'setup' && P.view) {
    const v = P.view;
    if (P.phase === 'battle' && !P.paused) {
      const now = performance.now();
      let step = now < P.stopUntil ? 0 : dt * BASE_SPEED * settings().speed;
      if (P.showT < P.slowUntil) step *= FEEL.slowFactor;
      let next = P.showT + step;
      advance(v, next);
      const auto = autoPauseIn(v.result, P.showT, next);
      if (auto) {
        next = Math.max(P.showT, auto.t);
        P.paused = true;
        setHint(auto.text, auto.kind);
        renderTop();
      }
      feel(v.result, P.showT, next);
      P.showT = next;
      if (ended(v) && P.showT >= duration(v) + AFTER) finish();
    } else if (P.phase === 'end' && P.showT < duration(v) + AFTER + 3) {
      P.showT += dt * BASE_SPEED;
    }
    advance(v, P.showT);
  }
  draw();
  barTick();
}

// ---------- Zeichnen ----------

function setupUnits(): DrawUnit[] {
  const friends: DrawUnit[] = practice.friends.map((f, i) => {
    const sp = getSpecies(f.species);
    return {
      uid: i,
      side: 'friend' as const,
      kind: 'friend' as const,
      name: f.name,
      short: '',
      color: CLASS_COLORS[f.friendClass]!,
      radius: sp.radius,
      x: P.placements[i]!.x,
      y: P.placements[i]!.y,
      hp: 1,
      maxHp: 1,
      mut: 0,
      flags: FrameFlag.alive,
      traits: [],
      art: f.species,
      friendClass: f.friendClass,
    };
  });
  return friends.concat(
    practice.enemies.map((e, j): DrawUnit => ({
      uid: 4 + j,
      side: 'enemy' as const,
      kind: 'stormling' as const,
      name: ENEMY_NAMES[j]!,
      short: '',
      color: '#6b6a8f',
      radius: getEnemy(e.enemy).stats.radius,
      x: e.x,
      y: e.y,
      hp: 1,
      maxHp: 1,
      mut: 0,
      flags: FrameFlag.alive,
      traits: [],
      art: e.enemy,
    })),
  );
}

function draw(): void {
  // T38: Auf dem Feld des Übungsufers steht alles in Zweigbuch
  const before = { ...FONT };
  FONT.body = 'Zweigbuch, system-ui, sans-serif';
  FONT.title = 'Zweigbuch, Georgia, serif';
  try {
    drawField();
  } finally {
    Object.assign(FONT, before);
  }
}

/**
 * Die Leinwand des Felds: ein ganzzahliges Vielfaches von 640 × 400 (4 Bildpixel je Feldeinheit), damit Boden,
 * Hindernisse und Figuren dieselbe Vergrößerung haben und die Bilder genau auf ihrer Kollision sitzen (Grafik-Lead,
 * Version 7). Der Browser passt sie weich an die Größe auf dem Bildschirm an.
 */
function setupField(c: HTMLCanvasElement): { ctx: CanvasRenderingContext2D; scale: number } {
  const dpr = window.devicePixelRatio || 1;
  const rect = c.getBoundingClientRect();
  const base = FIELD.width * pixelsPerUnit();
  const k = Math.max(1, Math.round((rect.width * dpr) / base));
  const w = base * k;
  const h = FIELD.height * pixelsPerUnit() * k;
  if (c.width !== w || c.height !== h) {
    c.width = w;
    c.height = h;
  }
  return { ctx: c.getContext('2d')!, scale: w / FIELD.width };
}

function drawField(): void {
  const { ctx, scale: s } = setupField(canvas);
  const T = P.phase === 'setup' ? performance.now() / 1000 : P.showT;
  ctx.save();
  if (performance.now() < P.shakeUntil) {
    const k = (P.shakeUntil - performance.now()) / FEEL.shakeMs;
    ctx.translate((Math.random() - 0.5) * FEEL.shakePx * k * 2, (Math.random() - 0.5) * FEEL.shakePx * k * 2);
  }
  ctx.clearRect(-20, -20, canvas.width + 40, canvas.height + 40);
  const r = P.view?.result ?? null;
  const rain = r ? rainAt(r, T) : 1;
  drawGround(ctx, s, P.phase === 'setup', rain, practice.stormLevel, () => drawPracticeGround(ctx, s, rain));
  const props = obstacleProps(ctx, s, OBSTACLES, T);
  if (P.phase === 'setup' || !r) {
    P.units = setupUnits();
    drawUnits(ctx, s, P.units, { highlight: null, showTraits: false, t: T, props });
    drawLeaves(ctx, s, T);
    drawPracticeLight(ctx, s);
    ctx.restore();
    const near = P.cursor.inside && P.placements.some((q) => Math.hypot(q.x - P.cursor.x, q.y - 2 - P.cursor.y) < 7);
    setCursor(P.drag ? cursorFor('zeiger-greifen', 'grabbing') : near ? cursorFor('zeiger-pfote', 'grab') : cursorFor('zeiger-pfote', 'default'));
    return;
  }
  P.units = unitsAt(r, T);
  for (const u of P.units) if (u.uid >= FRIENDS) u.name = ENEMY_NAMES[u.uid - FRIENDS] ?? u.name;
  const hoverUnit = P.cursor.inside ? unitAt(P.cursor) : null;
  P.hover = hoverUnit?.uid ?? null;
  drawGroundSigns(ctx, s, r, P.units, T);
  drawLandingShadows(ctx, s, r, T);
  // In der Pause wird das Feld etwas dunkler und ruhiger: der Boden deutlich, die Figuren nur ein wenig. Was am Boden
  // liegt (Ansagen, Befehle, Ringe), kommt danach und leuchtet; die hinteren Teile der Ringe liegen unter den Figuren
  const bright = P.paused || P.phase === 'end';
  const now = performance.now() / 1000;
  const telegraphs = telegraphsAt(r, T);
  const orders = ordersAt(r, T);
  const dim = (a: number) => {
    ctx.fillStyle = `rgba(12, 16, 22, ${a})`;
    ctx.fillRect(-20, -20, canvas.width + 40, canvas.height + 40);
  };
  if (P.paused && P.phase === 'battle') dim(0.34);
  drawTelegraphs(ctx, s, telegraphs, P.units, T, bright, 'ground');
  if (P.phase === 'battle') {
    drawOrders(ctx, s, orders, P.units, bright, P.selected, now, 'ground');
    drawRings(ctx, s, P.units, P.selected, P.hover, now, readyOf, 'ground');
  }
  drawUnits(ctx, s, P.units, { highlight: P.hover ?? P.selected, showTraits: true, t: T, props });
  drawArtEffects(ctx, s, P.units, r, T);
  const byUid = new Map(P.units.map((u) => [u.uid, u]));
  drawEffects(
    ctx,
    s,
    calmEvents(r),
    T,
    (uid) => {
      const u = byUid.get(uid);
      if (!u) return null;
      const g = figureGeo(u, s);
      const downLabel = u.side === 'friend' ? 'ruht sich aus' : 'vertrieben';
      return g ? { x: u.x, y: g.cy / s, r: (g.y - g.top) / s / 2 + 0.5, downLabel, escapeLabel: '', side: u.side } : { x: u.x, y: u.y, r: u.radius, downLabel, escapeLabel: '', side: u.side };
    },
    (id) => getAbility(id).name,
    (uid, t) => {
      const f = r.frames[Math.max(0, Math.floor(t / combatRules.tickSeconds) - 1)];
      const d = f?.units[uid];
      return d ? { x: d[0], y: d[1] } : null;
    },
    () => false,
    { figures: true },
  );
  drawLeaves(ctx, s, T);
  drawPracticeLight(ctx, s);
  drawBigs(ctx, s, T);
  if (P.paused && P.phase === 'battle') dim(0.08);
  drawTelegraphs(ctx, s, telegraphs, P.units, T, bright, 'top');
  drawBeckon(ctx, s, r, P.units, T);
  if (P.phase === 'battle') {
    drawOrders(ctx, s, orders, P.units, bright, P.selected, now, 'top');
    drawRings(ctx, s, P.units, P.selected, P.hover, now, readyOf, 'top');
    const pv = preview();
    if (pv) drawPreview(ctx, s, pv, now);
  }
  ctx.restore();
  // Mauszeiger: zielen, angreifen, hingehen, sonst die Pfote (Bilder des Grafik-Leads, wenn es sie gibt)
  const over = P.hover !== null ? P.units[P.hover] : null;
  setCursor(
    P.aiming ? cursorFor('zeiger-ziel', 'crosshair')
    : over?.side === 'friend' ? cursorFor('zeiger-pfote', 'pointer')
    : P.selected !== null && over?.side === 'enemy' ? cursorFor('zeiger-angriff', 'crosshair')
    : P.selected !== null ? cursorFor('zeiger-gehen', 'crosshair')
    : cursorFor('zeiger-pfote', 'default'),
  );
}

let lastCursor = '';
function setCursor(c: string): void {
  if (c === lastCursor) return;
  lastCursor = c;
  canvas.style.cursor = c;
}

function rainAt(r: BattleResult, T: number): number {
  let m = 1;
  for (const e of r.events) {
    if (e.t > T) break;
    if (e.type === 'heavyRain') m = e.multiplier;
  }
  return m;
}

/** Große Zahl bei einer Schwachstelle und beim Sturzflug: springt auf und sinkt langsam */
function drawBigs(ctx: CanvasRenderingContext2D, s: number, T: number): void {
  P.bigs = P.bigs.filter((b) => T - b.t < 1.6);
  for (const b of P.bigs) {
    const age = T - b.t;
    if (age < 0) continue;
    const pop = age < 0.12 ? 0.6 + (age / 0.12) * 0.7 : 1.3 - Math.min(0.3, (age - 0.12) * 0.6);
    ctx.save();
    ctx.globalAlpha = Math.max(0, 1 - Math.max(0, age - 1) / 0.6);
    ctx.font = `800 ${Math.round(s * (b.text.length > 5 ? 3.4 : 5.2) * pop)}px Zweigbuch, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.lineWidth = Math.max(3, s * 0.9);
    ctx.strokeStyle = 'rgba(40, 20, 10, 0.9)';
    const y = (b.y - age * 3) * s;
    ctx.strokeText(b.text, b.x * s, y);
    ctx.fillStyle = b.color;
    ctx.fillText(b.text, b.x * s, y);
    ctx.restore();
  }
}

// ---------- Leiste und Kopf ----------

function renderTop(): void {
  const pause = $('pr-pause');
  const label = P.paused ? 'Weiter' : 'Pause';
  if (pause.dataset.twigText !== label) setTwigText(pause, label);
  pause.classList.toggle('primary', P.paused);
  document.body.classList.toggle('pr-paused', P.paused && P.phase === 'battle');
  $('pr-auto').textContent = `Selbst anhalten: ${settings().autoPause ? 'an' : 'aus'}`;
  $('pr-auto').classList.toggle('active', settings().autoPause);
}

/** Bild der Figur (erstes Bild von stehen) in eine kleine Leinwand */
function portrait(species: string): HTMLCanvasElement {
  const c = document.createElement('canvas');
  const sh = sprite('species', species)?.sheet;
  const img = sh ? image(sh.file) : null;
  const [w, h] = sh?.frameSize ?? [24, 24];
  c.width = w;
  c.height = h;
  c.className = 'pr-face';
  const ctx = c.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;
  if (img && sh) {
    const row = sh.animations.stehen?.row ?? 0;
    ctx.drawImage(img, 0, row * h, w, h, 0, 0, w, h);
  }
  return c;
}

let barBuilt = false;
let lastBar = 0;
function renderBar(): void {
  const bar = $('pr-bar');
  if (!barBuilt || !bar.children.length) {
    bar.innerHTML = practice.friends
      .map((f, i) => {
        const k = getAbility(f.kniff);
        return `<div class="pr-card" data-uid="${i}">
          <span class="pr-key">${i + 1}</span>
          <kbd class="pr-qkey" title="Q zielt mit dem Kniff des gewählten Freunds">Q</kbd>
          <div class="pr-portrait"></div>
          <div class="pr-info">
            <b class="pr-name">${f.name}</b>
            <span class="pr-meter pr-hp"><i></i></span>
            <span class="pr-meter pr-mut"><i></i></span>
            <span class="pr-order"></span>
          </div>
          <button class="pr-kniff" data-kniff title="${k.text.replace(/"/g, '&quot;')}"><span class="pr-kicon-slot"></span>${k.name}</button>
        </div>`;
      })
      .join('');
    bar.querySelectorAll<HTMLElement>('.pr-card').forEach((card, i) => card.querySelector('.pr-portrait')!.append(portrait(practice.friends[i]!.species)));
    barBuilt = true;
  }
  updateBar();
}

/** Ein paar Mal je Sekunde: Leben, Mut, Befehl, Auswahl; einmal je Sekunde, ob neue Bilder fertig geladen sind */
let artSeen = '';
let lastArtCheck = 0;
function barTick(): void {
  const now = performance.now();
  if (now - lastArtCheck > 1000) {
    lastArtCheck = now;
    const seen = sprites().filter((sp) => sp.kind === 'ui' && image(sp.file)).length + ':' + sprites().length;
    if (seen !== artSeen) {
      artSeen = seen;
      applyFrameArt();
      updateBar();
    }
  }
  if (now - lastBar < 90) return;
  lastBar = now;
  updateBar();
}

function updateBar(): void {
  const r = P.view?.result;
  const f = r ? r.frames[frameIndex(r, P.showT)] : null;
  const orders = r ? ordersAt(r, P.showT) : new Map();
  document.querySelectorAll<HTMLElement>('#pr-bar .pr-card').forEach((card) => {
    const uid = Number(card.dataset.uid);
    const u = r?.units[uid];
    const d = f?.units[uid];
    const alive = !d || (d[4] & FrameFlag.alive) !== 0;
    const hp = d && u ? d[2] / u.maxHp : 1;
    const mut = d && u ? d[3] / u.mutMax : practice.friends[uid]!.startMut;
    const ready = !!d && ((d[8] ?? 0) & FrameFlag2.ready) !== 0 && alive;
    card.classList.toggle('selected', P.selected === uid);
    card.classList.toggle('ready', ready);
    card.classList.toggle('down', !alive);
    card.classList.toggle('aiming', P.aiming?.uid === uid);
    card.querySelector<HTMLElement>('.pr-hp i')!.style.width = `${Math.max(0, Math.min(1, hp)) * 100}%`;
    card.querySelector<HTMLElement>('.pr-hp')!.classList.toggle('low', hp < 0.35);
    card.querySelector<HTMLElement>('.pr-mut i')!.style.width = `${Math.max(0, Math.min(1, mut)) * 100}%`;
    const o = orders.get(uid);
    card.querySelector<HTMLElement>('.pr-order')!.textContent = !alive ? 'ruht sich aus' : o?.kind === 'move' ? 'geht dorthin' : o?.kind === 'attack' ? `greift ${ENEMY_NAMES[o.target - FRIENDS] ?? ''} an` : o?.kind === 'kniff' ? 'Kniff!' : ready ? 'Kniff bereit' : 'handelt selbst';
    (card.querySelector('.pr-kniff') as HTMLButtonElement).disabled = !ready || P.phase !== 'battle';
    // Das Zeichen des Kniffs (Bild des Grafik-Leads), Bild 1, sobald er bereit ist
    const slot = card.querySelector<HTMLElement>('.pr-kicon-slot')!;
    const want = `${artSeen}:${ready}`;
    if (slot.dataset.icon !== want) {
      slot.dataset.icon = want;
      const icon = kniffIcon(practice.friends[uid]!.kniff, ready);
      slot.replaceChildren(...(icon ? [icon] : []));
    }
  });
  const clock = $('pr-clock');
  if (P.phase === 'battle' || P.phase === 'end') {
    const t = Math.min(P.showT, P.view ? duration(P.view) : 0);
    const rain = r ? rainAt(r, t) : 1;
    clock.textContent = `${Math.floor(t)} s${rain > 1 ? ' · Starkregen' : ''}`;
  } else clock.textContent = 'Aufstellen';
}

/** Für die Konsole und Tests im Browser: wer wo steht */
export function practiceDebug(): unknown {
  const pick = (u: DrawUnit) => ({ uid: u.uid, name: u.kindName ?? u.name, x: u.x, y: u.y, hp: u.hp, maxHp: u.maxHp, alive: (u.flags & FrameFlag.alive) !== 0 });
  return { phase: P.phase, t: P.showT, paused: P.paused, friends: P.units.filter((u) => u.side === 'friend').map(pick), enemies: P.units.filter((u) => u.side === 'enemy' && (u.flags & FrameFlag.alive) !== 0).map(pick) };
}
